import React, { useMemo, useState } from "react";
import {
  Container,
  Row,
  Card,
  Col,
  Modal,
  Form,
  Button,
  Offcanvas,
  Badge,
} from "react-bootstrap";
import DataTable from "react-data-table-component";
import Breadcrumb from "Common/BreadCrumb";
import { Link, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import {
  useAddParentMutation,
  useDeleteParentMutation,
  useFetchParentsQuery,
  useUpdateParentMutation,
} from "features/parents/parentSlice";
import Select from "react-select";
import {
  useAddEtudiantMutation,
  useFetchEtudiantsQuery,
} from "features/etudiants/etudiantSlice";
import UpdateParent from "./UpdateParent";
import UpdatePassword from "./UpdatePassword";
import * as XLSX from "xlsx";

const Parents = () => {
  const { data = [] } = useFetchParentsQuery();
  const { data: AllEtudiants = [] } = useFetchEtudiantsQuery();

  const [showParent, setShowParent] = useState<boolean>(false);

  const [deleteParent] = useDeleteParentMutation();
  const notifySuccess = () => {
    Swal.fire({
      position: "center",
      icon: "success",
      title: "Le parent a été créé avec succès",
      showConfirmButton: false,
      timer: 2500,
    });
  };

  const notifyError = (err: any) => {
    Swal.fire({
      position: "center",
      icon: "error",
      title: `Une erreur est survenue: ${err}`,
      showConfirmButton: false,
      timer: 2500,
    });
  };

  const swalWithBootstrapButtons = Swal.mixin({
    customClass: {
      confirmButton: "btn btn-success",
      cancelButton: "btn btn-danger",
    },
    buttonsStyling: false,
  });

  const AlertDelete = async (_id: any) => {
    swalWithBootstrapButtons
      .fire({
        title: "Etes-vous sûr?",
        text: "Vous ne pouvez pas revenir en arrière?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Oui, supprime-le !",
        cancelButtonText: "Non, annuler !",
        reverseButtons: true,
      })
      .then((result) => {
        if (result.isConfirmed) {
          deleteParent(_id);
          swalWithBootstrapButtons.fire(
            "Supprimé !",
            "Le parent est supprimé.",
            "success"
          );
        } else if (result.dismiss === Swal.DismissReason.cancel) {
          swalWithBootstrapButtons.fire(
            "Annulé",
            "Le parent est en sécurité :)",
            "info"
          );
        }
      });
  };

  const [modal_AddParent, setmodal_AddParent] = useState<boolean>(false);
  function tog_AddParent() {
    setmodal_AddParent(!modal_AddParent);
  }
  const [modal_UpdateParent, setmodal_UpdateParent] = useState<boolean>(false);
  function tog_UpdateParent() {
    setmodal_UpdateParent(!modal_UpdateParent);
  }
  const [modal_UpdatePassword, setmodal_UpdatePassword] =
    useState<boolean>(false);
  function tog_UpdatePassword() {
    setmodal_UpdatePassword(!modal_UpdatePassword);
  }
  const filtredEtudiants = AllEtudiants.filter(
    (etudiant) => etudiant?.parent! === null
  );

  const optionColumnsTable = filtredEtudiants.map((etudiant: any) => ({
    value: etudiant?._id!,
    label: `${etudiant.nom} ${etudiant.prenom} _ ${etudiant?.classe
      ?.nom_classe!}`,
  }));

  const [selectedColumnValues, setSelectedColumnValues] = useState<any[]>([]);

  const handleSelectValueColumnChange = (selectedOption: any) => {
    const values = selectedOption.map((option: any) => option.value);
    setSelectedColumnValues(values);
  };

  const [createParent] = useAddParentMutation();

  const initialParent = {
    cin: "",
    nom_parent: "",
    prenom_parent: "",
    phone: "",
    username: "",
    password: "",
    fils: [
      {
        _id: "",
        nom: "",
        prenom: "",
        avatar: "",
      },
    ],
    profession: "",
  };

  const [parent, setParent] = useState(initialParent);

  const {
    cin,
    nom_parent,
    prenom_parent,
    phone,
    username,
    password,
    fils,
    profession,
  } = parent;

  const onChangeParent = (e: React.ChangeEvent<HTMLInputElement>) => {
    setParent((prevState) => ({
      ...prevState,
      [e.target.id]: e.target.value,
    }));
  };

  const onSubmitParent = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      const lastSixDigits = phone.slice(-6);
      const reversedLastSixDigits = lastSixDigits.split("").reverse().join("");

      parent["username"] = phone;
      parent["password"] = reversedLastSixDigits;
      parent["fils"] = selectedColumnValues;
      createParent(parent)
        .then(() => notifySuccess())
        .then(() => setParent(initialParent));
    } catch (error) {
      notifyError(error);
    }
  };

  // Stats
  const stats = useMemo(() => {
    const totalFils = data.reduce(
      (acc: number, p: any) => acc + (p.fils?.length || 0),
      0
    );
    const withPhone = data.filter((p: any) => p.phone && p.phone !== "").length;
    return { total: data.length, totalFils, withPhone };
  }, [data]);

  const columns = [
    {
      name: <span className="fw-semibold fs-13">Parent</span>,
      selector: (row: any) => row.nom_parent,
      cell: (row: any) => {
        const initials = `${(row.prenom_parent || "")[0] || ""}${(row.nom_parent || "")[0] || ""}`.toUpperCase();
        return (
          <div className="d-flex align-items-center gap-2">
            <div className="avatar-xs flex-shrink-0">
              <div className="avatar-title bg-primary-subtle text-primary rounded-circle fs-16">
                {initials || <i className="ri-user-3-line" />}
              </div>
            </div>
            <div>
              <span className="d-block fw-medium fs-13">
                {row.prenom_parent} {row.nom_parent}
              </span>
              {row.profession && (
                <span className="text-muted fs-12">{row.profession}</span>
              )}
            </div>
          </div>
        );
      },
      sortable: true,
      grow: 2,
    },
    {
      name: <span className="fw-semibold fs-13">C.I.N</span>,
      selector: (row: any) => row?.cin!,
      cell: (row: any) => (
        <span className="fs-13">
          <i className="ri-bank-card-line me-1 text-muted" />
          {row?.cin || "—"}
        </span>
      ),
      sortable: true,
    },
    {
      name: <span className="fw-semibold fs-13">Téléphone</span>,
      selector: (row: any) => row?.phone!,
      cell: (row: any) => (
        <span className="fs-13">
          <i className="ri-phone-line me-1 text-success" />
          {row?.phone || "—"}
        </span>
      ),
      sortable: true,
    },
    {
      name: <span className="fw-semibold fs-13">Enfant(s)</span>,
      selector: (row: any) => row?.fils?.length || 0,
      cell: (row: any) => (
        <div>
          {row.fils && row.fils.length > 0 ? (
            row.fils.map((f: any) => (
              <Badge
                key={f._id}
                bg={"info-subtle" as any}
                text={"info" as any}
                className="me-1 mb-1 fs-12 px-2 py-1"
              >
                <i className="ri-user-heart-line me-1" />
                {f.prenom} {f.nom}
              </Badge>
            ))
          ) : (
            <span className="text-muted fs-12">Aucun</span>
          )}
        </div>
      ),
      sortable: true,
      grow: 2,
    },
    {
      name: <span className="fw-semibold fs-13">Actions</span>,
      cell: (row: any) => (
        <div className="d-flex gap-1">
          <Link
            to="#"
            state={row}
            className="btn btn-soft-info btn-sm btn-icon"
            onClick={() => setShowParent(true)}
            title="Voir détails"
          >
            <i className="ri-eye-line" />
          </Link>
          <Link
            to="#"
            state={row}
            className="btn btn-soft-success btn-sm btn-icon"
            onClick={() => tog_UpdateParent()}
            title="Modifier"
          >
            <i className="ri-edit-2-line" />
          </Link>
          <Link
            to="#"
            state={row}
            className="btn btn-soft-warning btn-sm btn-icon"
            onClick={() => tog_UpdatePassword()}
            title="Mot de passe"
          >
            <i className="ri-shield-keyhole-line" />
          </Link>
          <button
            className="btn btn-soft-danger btn-sm btn-icon"
            onClick={() => AlertDelete(row._id)}
            title="Supprimer"
          >
            <i className="ri-delete-bin-2-line" />
          </button>
        </div>
      ),
    },
  ];

  const parentLocation = useLocation();

  const [searchTerm, setSearchTerm] = useState("");
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const getFilteredParents = () => {
    let filteredParents = data;

    if (searchTerm) {
      filteredParents = filteredParents.filter(
        (parent: any) =>
          parent
            ?.nom_parent!.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          parent
            ?.prenom_parent!.toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          parent?.phone!.toLowerCase().includes(searchTerm.toLowerCase()) ||
          parent?.cin!.toLowerCase().includes(searchTerm.toLowerCase()) ||
          parent?.username!.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return filteredParents;
  };

  const [isLoading, setIsLoading] = useState(false);
  const [updateParent] = useUpdateParentMutation();
  const [createEleve] = useAddEtudiantMutation();
  const handleFileUpload = async (e: any) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const data = await file.arrayBuffer();
    const workbook = XLSX.read(data, { type: "array" });
    const worksheet = workbook.Sheets[workbook.SheetNames[0]];
    const jsonData = XLSX.utils.sheet_to_json(worksheet);

    await processExcelData(jsonData as any[]);
  };

  const processExcelData = async (rows: any[]) => {
    for (const row of rows) {
      try {
        const telParent = row["Tel Parent"] ? String(row["Tel Parent"]) : "";
        const lastSixDigits = telParent.slice(-6);
        const reversedLastSixDigits = lastSixDigits
          .split("")
          .reverse()
          .join("");

        const parentPayload = {
          cin: "",
          nom_parent: "",
          prenom_parent: row["Nom parent FR"] || "",
          phone: telParent || "",
          username: telParent || "",
          password: reversedLastSixDigits,
          profession: "",
        };

        // 1. Create parent
        const parent = await createParent(parentPayload).unwrap();

        if (!parent._id) {
          console.error("Parent not created:", row);
          continue;
        }

        // 2. Create student (etudiant)
        const etudiantPayload = {
          nom: "",
          prenom: row["Nom Eleve FR"] || "",
          classe: row["Classe"] || "",
          parent: parent._id,
          genre: "",
          avatar: "",
          date_de_naissance: "",
          lieu_naissance: "",
          adresse_eleve: "",
          ville: "",
          nationalite: "",
          annee_scolaire: "",
          etablissement_frequente: "",
          moyenne_trimestre_1: "",
          moyenne_trimestre_2: "",
          moyenne_trimestre_3: "",
          moyenne_annuelle: "",
          moyenne_concours_6: "",
          numero_convocation_concours: "",
        };

        const student = await createEleve(etudiantPayload).unwrap();

        if (!student._id) {
          console.error("Student not created properly:", student);
          continue; // skip this row
        }

        // 3. Update parent by adding the new student to `fils` array
        const updatedFils = [
          {
            _id: student._id,
            nom: student.nom,
            prenom: student.prenom,
            avatar: student.avatar,
            classe: student.classe,
          },
        ];

        await updateParent({
          _id: parent._id,
          ...parentPayload,
          fils: updatedFils,
        });

      } catch (err) {
        console.error("Error processing row:", row, err);
      }
    }
  };

  const customStyles = {
    headCells: {
      style: {
        backgroundColor: "#f3f6f9",
        fontWeight: "600",
        fontSize: "13px",
        color: "#495057",
        borderBottom: "2px solid #e9ebec",
      },
    },
    rows: {
      style: {
        fontSize: "13px",
        "&:hover": {
          backgroundColor: "#f3f6f9",
        },
      },
    },
    pagination: {
      style: {
        borderTop: "1px solid #e9ebec",
      },
    },
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumb title="Parents" pageTitle="Tableau de bord" />

          {/* KPI Cards */}
          <Row className="mb-3">
            <Col sm={6} xl={4}>
              <Card className="card-animate border-0 shadow-sm">
                <Card.Body>
                  <div className="d-flex align-items-center">
                    <div className="avatar-sm flex-shrink-0">
                      <span className="avatar-title bg-primary-subtle text-primary rounded-circle fs-3">
                        <i className="ri-parent-line" />
                      </span>
                    </div>
                    <div className="flex-grow-1 ms-3">
                      <p className="text-uppercase fw-medium text-muted mb-1 fs-13">Total Parents</p>
                      <h4 className="mb-0">{stats.total}</h4>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col sm={6} xl={4}>
              <Card className="card-animate border-0 shadow-sm">
                <Card.Body>
                  <div className="d-flex align-items-center">
                    <div className="avatar-sm flex-shrink-0">
                      <span className="avatar-title bg-success-subtle text-success rounded-circle fs-3">
                        <i className="ri-user-heart-line" />
                      </span>
                    </div>
                    <div className="flex-grow-1 ms-3">
                      <p className="text-uppercase fw-medium text-muted mb-1 fs-13">Total Enfants</p>
                      <h4 className="mb-0 text-success">{stats.totalFils}</h4>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col sm={6} xl={4}>
              <Card className="card-animate border-0 shadow-sm">
                <Card.Body>
                  <div className="d-flex align-items-center">
                    <div className="avatar-sm flex-shrink-0">
                      <span className="avatar-title bg-info-subtle text-info rounded-circle fs-3">
                        <i className="ri-phone-line" />
                      </span>
                    </div>
                    <div className="flex-grow-1 ms-3">
                      <p className="text-uppercase fw-medium text-muted mb-1 fs-13">Avec Téléphone</p>
                      <h4 className="mb-0 text-info">{stats.withPhone}</h4>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Data Table */}
          <Col lg={12}>
            <Card className="border-0 shadow-sm">
              <Card.Header className="border-bottom-dashed bg-white">
                <Row className="g-3 align-items-center">
                  <Col lg={4} md={6}>
                    <div className="search-box">
                      <input
                        type="text"
                        className="form-control search"
                        placeholder="Rechercher par nom, prénom, CIN, téléphone..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                      />
                      <i className="ri-search-line search-icon"></i>
                    </div>
                  </Col>
                  <Col lg={3} md={3}>
                    <input
                      type="file"
                      accept=".xlsx, .xls"
                      className="form-control"
                      onChange={handleFileUpload}
                    />
                  </Col>
                  <Col lg={5} md={3} className="d-flex justify-content-end gap-2">
                    {searchTerm && (
                      <Button
                        variant="soft-secondary"
                        size="sm"
                        onClick={() => setSearchTerm("")}
                      >
                        <i className="ri-filter-off-line me-1" />
                        Réinitialiser
                      </Button>
                    )}
                    <Button
                      variant="primary"
                      onClick={() => tog_AddParent()}
                      className="d-flex align-items-center gap-1"
                    >
                      <i className="ri-add-line fs-16" />
                      Nouveau parent
                    </Button>
                  </Col>
                </Row>
              </Card.Header>
              <Card.Body className="p-0">
                <DataTable
                  columns={columns}
                  data={getFilteredParents()}
                  pagination
                  paginationPerPage={15}
                  paginationRowsPerPageOptions={[10, 15, 25, 50]}
                  customStyles={customStyles}
                  noDataComponent={
                    <div className="text-center py-5">
                      <i className="ri-parent-line fs-1 text-muted d-block mb-2" />
                      <p className="text-muted mb-0">Aucun parent trouvé</p>
                    </div>
                  }
                  paginationComponentOptions={{
                    rowsPerPageText: "Lignes par page",
                    rangeSeparatorText: "sur",
                  }}
                  highlightOnHover
                  responsive
                />
              </Card.Body>
            </Card>
          </Col>

          {/* Add Modal */}
          <Modal
            className="fade"
            id="createModal"
            show={modal_AddParent}
            onHide={() => {
              tog_AddParent();
            }}
            centered
            size="lg"
          >
            <Modal.Header closeButton className="bg-primary-subtle">
              <h5 className="modal-title">
                <i className="ri-add-circle-line me-2" />
                Ajouter Parent
              </h5>
            </Modal.Header>
            <Modal.Body>
              <Form className="create-form" onSubmit={onSubmitParent}>
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Label htmlFor="nom_parent">Nom</Form.Label>
                    <Form.Control
                      type="text"
                      id="nom_parent"
                      name="nom_parent"
                      onChange={onChangeParent}
                      value={parent.nom_parent}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label htmlFor="prenom_parent">Prénom</Form.Label>
                    <Form.Control
                      type="text"
                      id="prenom_parent"
                      name="prenom_parent"
                      onChange={onChangeParent}
                      value={parent.prenom_parent}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label htmlFor="cin">C.I.N</Form.Label>
                    <Form.Control
                      type="text"
                      id="cin"
                      name="cin"
                      onChange={onChangeParent}
                      value={parent.cin}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label htmlFor="phone">Téléphone</Form.Label>
                    <Form.Control
                      type="text"
                      id="phone"
                      name="phone"
                      onChange={onChangeParent}
                      value={parent.phone}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label htmlFor="fils">Enfant(s)</Form.Label>
                    <Select
                      closeMenuOnSelect={false}
                      isMulti
                      options={optionColumnsTable}
                      onChange={handleSelectValueColumnChange}
                      placeholder="Sélectionner les enfants..."
                      noOptionsMessage={() => "Aucun élève disponible"}
                    />
                  </Col>
                  <Col md={6}>
                    <Form.Label htmlFor="profession">Profession</Form.Label>
                    <Form.Control
                      type="text"
                      id="profession"
                      name="profession"
                      onChange={onChangeParent}
                      value={parent.profession}
                    />
                  </Col>
                </Row>
                <div className="hstack gap-2 justify-content-end mt-4">
                  <Button
                    variant="light"
                    onClick={() => {
                      tog_AddParent();
                      setParent(initialParent);
                    }}
                  >
                    Annuler
                  </Button>
                  <Button
                    onClick={() => {
                      tog_AddParent();
                    }}
                    type="submit"
                    variant="primary"
                    id="addNew"
                  >
                    <i className="ri-check-line me-1" />
                    Enregistrer
                  </Button>
                </div>
              </Form>
            </Modal.Body>
          </Modal>

          {/* Update Modal */}
          <Modal
            className="fade"
            id="createModal"
            show={modal_UpdateParent}
            onHide={() => {
              tog_UpdateParent();
            }}
            centered
            size="lg"
          >
            <Modal.Header closeButton className="bg-success-subtle">
              <h5 className="modal-title">
                <i className="ri-edit-2-line me-2" />
                Modifier Parent
              </h5>
            </Modal.Header>
            <Modal.Body>
              <UpdateParent
                modal_UpdateParent={modal_UpdateParent}
                setmodal_UpdateParent={setmodal_UpdateParent}
              />
            </Modal.Body>
          </Modal>

          {/* Update Password Modal */}
          <Modal
            className="fade"
            id="updatePasswordModal"
            show={modal_UpdatePassword}
            onHide={() => {
              tog_UpdatePassword();
            }}
            centered
          >
            <Modal.Header closeButton className="bg-warning-subtle">
              <h5 className="modal-title">
                <i className="ri-shield-keyhole-line me-2" />
                Modifier Mot de passe
              </h5>
            </Modal.Header>
            <Modal.Body>
              <UpdatePassword
                modal_UpdatePassword={modal_UpdatePassword}
                setmodal_UpdatePassword={setmodal_UpdatePassword}
              />
            </Modal.Body>
          </Modal>
        </Container>

        {/* Detail Offcanvas */}
        <Offcanvas
          show={showParent}
          onHide={() => setShowParent(false)}
          placement="end"
          style={{ width: "420px" }}
        >
          <Offcanvas.Header closeButton className="border-bottom bg-light">
            <Offcanvas.Title className="d-flex align-items-center gap-2">
              <i className="ri-parent-line text-primary fs-20" />
              Détails du parent
            </Offcanvas.Title>
          </Offcanvas.Header>
          <Offcanvas.Body>
            {/* Parent avatar and name */}
            <div className="text-center mb-4 pt-2">
              <div className="avatar-md mx-auto mb-3">
                <div className="avatar-title bg-primary-subtle text-primary rounded-circle fs-1">
                  {(parentLocation?.state?.prenom_parent || "")[0]?.toUpperCase() || ""}
                  {(parentLocation?.state?.nom_parent || "")[0]?.toUpperCase() || ""}
                </div>
              </div>
              <h5 className="mb-1">
                {parentLocation?.state?.prenom_parent} {parentLocation?.state?.nom_parent}
              </h5>
              {parentLocation?.state?.profession && (
                <p className="text-muted mb-0">{parentLocation?.state?.profession}</p>
              )}
            </div>

            <div className="mb-4">
              <label className="text-muted text-uppercase fs-11 fw-semibold mb-2 d-block">
                C.I.N
              </label>
              <span>
                <i className="ri-bank-card-line me-1 text-primary" />
                {parentLocation?.state?.cin || "—"}
              </span>
            </div>

            <div className="mb-4">
              <label className="text-muted text-uppercase fs-11 fw-semibold mb-2 d-block">
                Téléphone
              </label>
              <span>
                <i className="ri-phone-line me-1 text-success" />
                {parentLocation?.state?.phone || "—"}
              </span>
            </div>

            <div className="mb-4">
              <label className="text-muted text-uppercase fs-11 fw-semibold mb-2 d-block">
                Enfant(s)
              </label>
              {parentLocation?.state?.fils?.map((fils: any) => (
                <div key={fils._id} className="d-flex align-items-center gap-2 mb-2">
                  <div className="avatar-xs">
                    {fils.avatar ? (
                      <img
                        src={`${process.env.REACT_APP_BASE_URL}/etudiantFiles/${fils.avatar}`}
                        alt=""
                        className="rounded-circle avatar-xs"
                      />
                    ) : (
                      <div className="avatar-title bg-info-subtle text-info rounded-circle">
                        {(fils.prenom || "")[0]?.toUpperCase()}{(fils.nom || "")[0]?.toUpperCase()}
                      </div>
                    )}
                  </div>
                  <span className="fw-medium">{fils.prenom} {fils.nom}</span>
                </div>
              ))}
            </div>
          </Offcanvas.Body>
        </Offcanvas>
      </div>
    </React.Fragment>
  );
};
export default Parents;
